// Step 3 N / steps 5-7 §12e (7.4.4b-1b): the press of a Canvas switch (a Component's action bound to a World door act), as
// every host sends it: the Desk's server (apps/server worldDoorHttp.ts canvasDoorActHandler) and a browser window with no Mac
// (apps/web webHostWorldDoors.ts), each through its own transport to the World's one /mcp door.
//
// The World is asked again what this press makes (action-prepare: the act's tool and arguments from the saved binding and
// this input, and its digest); that one call is then sent to the World's door, as the person, with the press it is (the
// x-neucleos-canvas-act header), and the World takes its declared steps: the card in the Inbox decides, never this press.
// Answers "applied", or "waiting" while the card waits (the host sends the same press again once it is decided), or the
// refusal. Nothing here decides anything: every check is the World's.

/** What a host's transport answers for one World tool call. */
export type DoorCallOutcome =
  | { readonly ok: true; readonly value: unknown }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

/** One World tool call: the press header goes with it when named; `prepare` marks the action-prepare call. */
export type DoorCall = (
  tool: string,
  args: Record<string, unknown>,
  options: { readonly canvasAct?: string; readonly prepare?: true },
) => Promise<DoorCallOutcome>;

/** A two-step act's first answer (the change it proposed), kept per press by a host that sends two-step acts. */
export interface ProposedStore {
  get(requestKey: string): { readonly changeKey: string; readonly digest: string } | null;
  set(requestKey: string, change: { readonly changeKey: string; readonly digest: string }): void;
}

export type PressOutcome =
  | {
      readonly ok: true;
      readonly status: "applied";
    }
  | {
      readonly ok: true;
      readonly status: "waiting";
      /** The World's sentence. */
      readonly reason: string;
      /** The waiting approval's key, when the sentence names it. */
      readonly waitingKey: string | null;
      /** The exact call that waits. */
      readonly call: { readonly tool: string; readonly args: Record<string, unknown> };
    }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

/** The World reads a press of at most this many characters (backend lib/canvasDoorActions.js CANVAS_ACT_HEADER_MAX). */
export const CANVAS_ACT_HEADER_MAX = 16_384;
export const CANVAS_ACT_HEADER = "x-neucleos-canvas-act";
const CANVAS_TOOL = "neucleos_canvas";
const GATE_WAITING = /\[GATE_AWAITING\]/u;

/**
 * The press header: the press with its digest, ASCII only (a header is Latin-1): every other character is written as its JSON
 * \u escape, which the World reads back. By UTF-16 code unit (no u flag, W-8f15f0fe), so a character above U+FFFF is written
 * as its two escaped surrogate halves, which JSON.parse joins again.
 */
export const canvasActHeader = (press: Record<string, unknown>, digest: string): string =>
  JSON.stringify({ ...press, digest }).replace(
    /[\u007f-￿]/g,
    (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, "0")}`,
  );

/** The Canvas door answers a waiting step as `gateRefusal` (a sentence, or { code, message }); other doors as a refusal. */
export const gateRefusalText = (value: unknown): string | null => {
  const refusalValue = (value as { gateRefusal?: unknown } | null)?.gateRefusal;
  if (typeof refusalValue === "string") return refusalValue;
  if (refusalValue && typeof refusalValue === "object") {
    const { code, message } = refusalValue as { code?: unknown; message?: unknown };
    return `REFUSED [${String(code ?? "GATE_REFUSED")}]: ${String(message ?? "")}`;
  }
  return null;
};

/** The gate instance key a waiting answer names ("… instance <key> awaits a human decision …"), or null. */
export function waitingInstanceKey(sentence: string): string | null {
  return /\binstance ([0-9a-f]{64}) awaits\b/u.exec(sentence)?.[1] ?? null;
}

const requiredString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value : null;

/**
 * One press, end to end. `press` is { canvasId, sequence, nodeId, name, input, requestKey } (anything else the World refuses);
 * `proposed` keeps a two-step act's change for its press, for a host that sends two-step acts (a host without it is never
 * answered one: the World refuses its prepare first).
 */
export async function pressCanvasSwitch(input: {
  readonly press: Record<string, unknown>;
  readonly call: DoorCall;
  readonly proposed?: ProposedStore;
}): Promise<PressOutcome> {
  const { press, call } = input;
  const requestKey = requiredString(press["requestKey"]);
  if (
    !requestKey ||
    !requiredString(press["canvasId"]) ||
    !requiredString(press["nodeId"]) ||
    !requiredString(press["name"])
  )
    return {
      ok: false,
      kind: "bad_request",
      reason: "A switch names its Canvas, placement, action and request identity.",
    };
  const prepared = await call(
    CANVAS_TOOL,
    { ...press, action: "action-prepare" },
    { prepare: true },
  );
  if (!prepared.ok) return prepared;
  const door = prepared.value as {
    status?: unknown;
    digest?: unknown;
    door?: { tool?: unknown; args?: unknown; then?: unknown };
  } | null;
  if (
    door?.status !== "door" ||
    typeof door.digest !== "string" ||
    typeof door.door?.tool !== "string" ||
    !door.door.args ||
    typeof door.door.args !== "object"
  )
    return {
      ok: false,
      kind: "upstream_refusal",
      reason: "This action is not a switch sent to the World's door.",
    };
  let next = { tool: door.door.tool, args: door.door.args as Record<string, unknown> };
  const header = canvasActHeader(press, door.digest);
  if (header.length > CANVAS_ACT_HEADER_MAX)
    return {
      ok: false,
      kind: "bad_request",
      reason:
        "This switch's input is too large to send to the World. Nothing was asked or written.",
    };
  // Step 3 P: a two-step act (backend lib/canvasDoorActions.js `then`): its first call prepares the change (no step, nothing
  // applied), and the second applies exactly the change the first answered with, so its card opens. Both go with the press;
  // the World checks each against it. A press again finds the same open change.
  const then = door.door.then;
  if (typeof then === "string") {
    if (!input.proposed)
      return {
        ok: false,
        kind: "upstream_refusal",
        reason: "This switch is not pressed here. Nothing was asked or written.",
      };
    const remembered = input.proposed.get(requestKey);
    if (remembered === null) {
      const first = await call(next.tool, next.args, { canvasAct: header });
      if (!first.ok) return first;
      const change = first.value as { changeKey?: unknown; digest?: unknown } | null;
      if (typeof change?.changeKey !== "string" || typeof change.digest !== "string")
        return {
          ok: false,
          kind: "upstream_refusal",
          reason: "The World did not prepare this change. Nothing was asked or written.",
        };
      input.proposed.set(requestKey, { changeKey: change.changeKey, digest: change.digest });
      next = { tool: then, args: { changeKey: change.changeKey, digest: change.digest } };
    } else {
      // Round 1 (P2): a press already prepared its change; every later send applies that same change, so a decline ends the
      // press (the change is closed) and a lost success reads back as done, never as a fresh change.
      next = { tool: then, args: { changeKey: remembered.changeKey, digest: remembered.digest } };
    }
  }
  const outcome = await call(next.tool, next.args, { canvasAct: header });
  const gateRefusal = outcome.ok ? gateRefusalText(outcome.value) : null;
  const sentence = !outcome.ok ? outcome.reason : gateRefusal;
  if (sentence !== null && GATE_WAITING.test(sentence))
    return {
      ok: true,
      status: "waiting",
      reason: sentence,
      waitingKey: waitingInstanceKey(sentence),
      call: next,
    };
  if (!outcome.ok) return outcome;
  if (gateRefusal !== null) return { ok: false, kind: "upstream_refusal", reason: gateRefusal };
  return { ok: true, status: "applied" };
}
